import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, Input } from '@angular/core';
import { ActivatedRoute, Router, Data } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subscription, Observable, BehaviorSubject, of } from "rxjs";
import { tap, switchMap, delay } from "rxjs/operators";
import { LoaderService } from "../../../../common/services/loader.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { AuthService } from "../../../../services/auth.service";
import { ApplicationNotesService } from "../../../../applications/services/application-notes.service";
import { ApplicationsService } from "../../../../applications/services/applications.service";
import { SidePanelService } from "../../../../common/services/side-panel.service";
import { ApplicationSteps, WorkflowData, ApplicationState, ApplicationStateChangeRequest } from "../../../../applications/models/applications.models";
import { ApplicationBaseComponent } from '../../../../applications/components/application-base.component';
import { WellSelectorComponent } from "../../well-selector/well-selector.component";
import { Well, GISPointTypes } from "../../../../common/models/well.model";
import { ApplicationConfigService, ApplicationsConfig, ModuleConfig } from "../../../../services/application-config.service";
import { ComplianceReportingType, WellSelectionMode } from '../../../models/compliance.model';
import { IssuesService } from '../../../../common/services/issues.service';
import { OnBaseDocumentTypeProcessReference } from '../../../../common/models/onBaseIntegrationAndMapping.model';

@Component({
    selector: 'app-as-drilled-plat-review',
    templateUrl: './as-drilled-plat-review.component.html',
    styleUrls: ['./as-drilled-plat-review.component.scss'],
    standalone: false
})
export class AsDrilledPlatReviewComponent extends ApplicationBaseComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() onBaseDocumentTypes: OnBaseDocumentTypeProcessReference[] = [];

    @ViewChild(WellSelectorComponent) private _selector: WellSelectorComponent;

    acceptDialog: Dialog = new Dialog("Application has been accepted", [new DialogButton("OK", true, () => { this.uploadDocumentsAndGoHome(); })]);

    private _config: ApplicationsConfig;

    get config(): ApplicationsConfig {
        return this._config;
    }

    defaultType: ComplianceReportingType = { typeId: "", description: "--Select--", apiNumbers: WellSelectionMode.None, isAnnual: false, isQuarterly: false } as ComplianceReportingType

    get isLoading(): boolean {
        return this.loading$.value;
    }

    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    loadingObs$: Observable<boolean> = this.loading$.asObservable();

    get organization(): any {
        if (this.form == null) return null;      
        let ctrl = this.form.get('organization');
        if (ctrl == null) return null;
        let id = this.form.get('organization').value;
        let name = this.form.get('organizationName').value;
        let rbdms = this.form.get('rbdmsNumber').value;
        return {
            id: id,
            name: name,
            historicalOwnerNumber: rbdms
        };
    }

    @Input() reportType: ComplianceReportingType;

    returnDialog: Dialog = new Dialog("Application #[APPNUMBER] has been [STATE]", [new DialogButton("OK", true, () => { this.home(); })]);


    get selectedWell(): string {
        if (this.form == null) return "";
        let ctrl = this.form.get('selectedWell');
        if (ctrl == null) return "";
        return ctrl.value;
    }


    get step(): ApplicationSteps {
        if (this.form == null) return ApplicationSteps.SelectWell;
        let ctrl = this.form.get('step');
        if (ctrl == null) return ApplicationSteps.SelectWell;
        return <ApplicationSteps>ctrl.value;
    }

    subheader: SubHeader = new SubHeader({ title: "", buttons: [] });


    get subheaderText(): string {
        return "Compliance Report #[XXX]";
    }

    @Input() well: Well;

    private _validPoints: GISPointTypes[] = [
        GISPointTypes.WellHead,
        GISPointTypes.Heel,
        GISPointTypes.Toe,
        GISPointTypes.Target
    ];

    constructor(loader: LoaderService, dialog: DynamicDialogService, subheader: SubHeaderService, auth: AuthService, appService: ApplicationsService, private configService: ApplicationConfigService,
        router: Router, sidePanelSvc: SidePanelService, noteSvc: ApplicationNotesService, private builder: FormBuilder, private route: ActivatedRoute, private issuesService: IssuesService) {
        super(loader, dialog, subheader, auth, appService, router, sidePanelSvc, noteSvc);
        this.numericFields = ['unitization|platSize', 'wellInformation|points|wellHead|latitude', 'wellInformation|points|wellHead|longitude', 'wellInformation|points|target|latitude', 'wellInformation|points|target|longitude', 'wellInformation|points|toe|latitude', 'wellInformation|points|toe|longitude', 'wellInformation|points|heel|latitude', 'wellInformation|points|heel|longitude'];
        this.fileFields = ['documentation|finalAsDrilledPlat', 'documentation|preliminaryPlat', 'documentation|boreholeDeviationSurvey', 'documentation|revisedUnitPlat', 'documentation|royaltyInterestList'];
        this.trueBooleanFields = ['wellInformation|points|wellHead|queryRun', 'wellInformation|points|wellHead|coordinatesValidated', 'wellInformation|points|heel|queryRun', 'wellInformation|points|heel|coordinatesValidated', 'wellInformation|points|toe|queryRun', 'wellInformation|points|toe|coordinatesValidated', 'wellInformation|points|target|queryRun', 'wellInformation|points|target|coordinatesValidated', 'certification']
        this.paymentFields = ['payments|payments'];
        this.authorizationFields = ['payments|creditAuthorization'];
        this.dateFields = [ 'review|totalDepthDate', 'review|asDrilledPlatPreliminaryDate', 'review|asDrilledPlatFinalDate', 'review|boreholeDeviationSurveyDate', 'review|revisedUnitDate', 'review|asDrilledFeeDate', 'review|revisedUnitFeeDate'];
    }

    ngOnInit() {
        this.confirmDialog = new Dialog("Any unsubmitted changes will be lost upon leaving this page.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.allowDeactivate.next(true); }), new DialogButton("No", false, () => { return false; })])

        let loadingObs$ = this.loadingObs$.pipe(
            delay(1000)
        ).subscribe(
            (isLoading: boolean) => {
                if (isLoading == false) this.restoreWells();
            },
            (error) => {
                this.handleError(error);
            });

        this.subscription.add(loadingObs$);

        let obs$ : Observable<ModuleConfig> = this.configService.getModuleConfig().pipe(
            tap((conf: ModuleConfig) => {
                if (conf != null && conf.applications != null) {
                    this._config = conf.applications;
                    if (this._config.permits != null && this._config.permits.documents != null) this.documentConfig = this._config.permits.documents;
                }
            })
        );

        let subs$: Subscription = obs$.pipe()
            .subscribe(
            () => {
                    this.subheader.title = this.subheaderText.replace("[XXX]", this.application.applicationNumber);
                    this.subheader.buttons.push({ name: "View Notes & Timeline", method: () => this.viewTimeline() });
                    if (this.application.applicationState != ApplicationState.Approved && this.application.applicationState != ApplicationState.Corrections) {
                        this.subheader.buttons.push({ name: "Accept", method: () => this.acceptApplication() });
                        this.subheader.buttons.push({ name: "Return", method: () => this.returnApplication() });
                        this.subheader.buttons.push({ name: "Save", method: () => this.save(true) });
                    }
                    this.subheaderService.updateSubHeader(this.subheader);
                    this.form = this.restoreFormData(this.buildForm());
                    if (this.well != null && this.well.location != null && (this.form.get('review').get('sourceOfLocation').value == null || this.form.get('review').get('sourceOfLocation').value == "")) {
                        this.form.get('review').get('sourceOfLocation').setValue(this.well.location.sourceOfLocation);
                    }
                    this.loading$.next(false);
                    this.form.disable();
                    this.form.get('review').enable();
                    this.setStep(ApplicationSteps.Review);
            },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            }
        );
        this.subscription.add(subs$);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    ngAfterViewInit() {
       
    }

    acceptApplication(): void {
        let obs$: Observable<WorkflowData> = this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {
                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: ApplicationState.Approved
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );
        this._loader.toggleLoader(true);
        let subs$ = obs$.subscribe( //need to work in uploading any documents here
            () => {
                this._loader.toggleLoader(false);
                this.acceptDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.acceptDialog);
            },
            (error) => {
                this.handleError(error);
            }
        );
        this.subscription.add(subs$);
    }

    buildForm(): FormGroup {
        return this.builder.group({
            organizationName: ["", []],
            organization: ["", []],
            rbdmsNumber: ["", []],
            wellName: ["", []],
            wellNumber: [null, []],
            reportType: [null, []],
            step: [ApplicationSteps.Review, []],
            selectedWell: [null, [Validators.required]],
            apiNumber: [null, [Validators.required]],

            unitization: this.builder.group({
                subjectToOrder: [null, []],
                orderNumber: [{ value: null, disabled: true }, []],
                platSize: [null, []]
            }),
            documentation: this.builder.group({
                finalAsDrilledPlat: [[], []],
                preliminaryPlat: [[], []],
                boreholeDeviationSurvey: [[], []],
                revisedUnitPlat: [[], []],
                royaltyInterestList: [[], []]
            }),
            wellInformation: this.builder.group({
                apiNumber: [null, [Validators.required]],
                wellName: [null, []],
                wellNumber: [null, []],
                slant: [null, [Validators.required]],
                points: this.buildWellPointsForm()
            }),
            payments: this.builder.group({
                option: [null, [Validators.required]],
                paymentType: [null, []],
                payments: [[], []],
                creditAuthorization: [null, []],
                invoice: [null, []]
            }),
            review: this.builder.group({
                totalDepthDate: [null, []],
                sourceOfLocation: [[], []],
                asDrilledPlatPreliminaryDate: [null, []],
                asDrilledPlatFinalDate: [null, []],
                boreholeDeviationSurveyDate: [null, []],
                revisedUnitDate: [null, []],
                asDrilledFeeDate: [null, []],
                revisedUnitFeeDate: [null, []]
            })
        });
    }

    buildWellPointsForm(): FormGroup {
        let subForm: FormGroup = null
        let obj = {}
        this._validPoints.forEach(p => {
            let gp = this.builder.group({
                latitude: [null, [Validators.min(0)]],
                longitude: [null, [Validators.max(0)]],
                countyNumber: [{ value: null, disabled: true }, []],
                countyName: [{ value: null, disabled: true }, []],
                townshipName: [{ value: null, disabled: true }, []],
                townshipNumber: [{ value: null, disabled: true }, []],
                civilTownshipNumber: [{ value: null, disabled: true }, []],
                quadrangleName: [{ value: null, disabled: true }, []],
                quadrangleCode: [{ value: null, disabled: true }, []],
                section: [{ value: null, disabled: true }, []],
                lot: [{ value: null, disabled: true }, []],
                tract: [{ value: null, disabled: true }, []],
                queryRun: [false, []],
                coordinatesValidated: [true, [Validators.requiredTrue]]
            });
            obj[p] = gp;
        });
        subForm = this.builder.group(obj);
        return subForm;
    }

    onWellsSelected(evt: Well[]): void {
        //should only ever receive one well, but selector emits a collection so handle as a collection
        let depthCtrl = this.form.get('review').get('totalDepthDate');
        let locCtrl = this.form.get('review').get('sourceOfLocation');
        evt.forEach(w => {
            if (depthCtrl != null) depthCtrl.setValue(w.dateTotalDepthReached);
            if (locCtrl != null && w.location != null && (locCtrl.value == null || w.location.sourceOfLocation != locCtrl.value)) {
                let source = w.location.sourceOfLocation;
                if (source == null) source = "";
                if (locCtrl.value == null || locCtrl.value.length == 0) locCtrl.setValue(source);

            }
        });
    }

    restoreWells(): void {
        if (this.application != null) {
            if (this._selector != null) {
                this._selector.reportedWells = [{ apiNumber: this.selectedWell, reviewStatus: "Pending" }];
                this._selector.findWells();
            }
            else {
                console.error("no selector component ready");
            }
        }
    }

    returnApplication(): void {
        let obs$: Observable<WorkflowData> = this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {
                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: ApplicationState.Corrections
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );
        this._loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this.returnDialog.message = this.returnDialog.message.replace('[APPNUMBER]', app.applicationNumber);
                    let state: string = app.applicationState == ApplicationState.Corrections
                        ? "returned for corrections"
                        : "returned for additional review";
                    this.returnDialog.message = this.returnDialog.message.replace('[STATE]', state);
                    this.returnDialog.toggleDialog(true);
                    this._loader.toggleLoader(false);
                    this.allowDeactivate.next(true);
                    this.dialogService.updateDialog(this.returnDialog);
                },
                (error) => {
                    console.error(error);
                    this.dialogService.showError();
                    this._loader.toggleLoader(false);
                }
            );
        this.subscription.add(subs$);
    }

    setStep(newStep: ApplicationSteps): void {
        this.form.get('step').setValue(newStep);
    }

    uploadDocumentsAndGoHome(): void {
        let upload$: Subscription = this.dataService.commitAssociatedDocuments(this.application.applicationNumber, false).pipe()
            .subscribe(
                () => { },
                (error) => {
                    this.handleError(error);
                }
        );
        this.subscription.add(upload$);
        setTimeout(() => { this.home(); }, 1500); //timeout before redirecting to give subscription a chance to fire
    }
}
